import { ImageIcon, Loader2, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import type { MeAccount } from "@/adapters/api";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	useDeleteAccountLogo,
	useUploadAccountLogo,
} from "../../hooks/use-account-logo.ts";
import { rdoErrorMessage } from "../../utils/errors.ts";

export const MAX_LOGO_BYTES = 1024 * 1024;
export const LOGO_TYPES = ["image/png", "image/jpeg"];

export function isAllowedLogo(file: File): boolean {
	return LOGO_TYPES.includes(file.type) && file.size <= MAX_LOGO_BYTES;
}

export function AccountLogoCard({ account }: { account: MeAccount }) {
	const inputRef = useRef<HTMLInputElement>(null);
	const upload = useUploadAccountLogo(account.id);
	const remove = useDeleteAccountLogo(account.id);
	const [error, setError] = useState<string | null>(null);
	const [confirmRemove, setConfirmRemove] = useState(false);

	const isPending = upload.isPending || remove.isPending;

	async function handleFile(files: FileList | null) {
		const file = files?.[0];
		if (!file) return;
		if (!isAllowedLogo(file)) {
			setError("Envie um PNG ou JPEG de até 1 MB.");
			return;
		}
		setError(null);
		try {
			await upload.mutateAsync(file);
			toast.success("Logo atualizado com sucesso!");
		} catch (err) {
			setError(rdoErrorMessage(err, "Não foi possível enviar o logo."));
		}
	}

	async function handleRemove() {
		try {
			await remove.mutateAsync();
			toast.success("Logo removido.");
			setConfirmRemove(false);
		} catch (err) {
			setConfirmRemove(false);
			setError(rdoErrorMessage(err, "Não foi possível remover o logo."));
		}
	}

	return (
		<Card className="account-logo-card">
			<CardHeader>
				<CardTitle>Logo da conta</CardTitle>
				<p className="account-logo-card__description">
					Impresso no cabeçalho de todos os Relatórios de Obra da conta. PNG ou JPEG de até
					1 MB.
				</p>
			</CardHeader>
			<CardContent className="account-logo-card__content">
				<div className="account-logo-card__preview" data-empty={!account.logo_url}>
					{account.logo_url ? (
						<img src={account.logo_url} alt={`Logo de ${account.name}`} />
					) : (
						<>
							<ImageIcon aria-hidden />
							<span>Sem logo</span>
						</>
					)}
				</div>
				<div className="account-logo-card__actions">
					<input
						ref={inputRef}
						type="file"
						accept={LOGO_TYPES.join(",")}
						aria-label="Arquivo do logo"
						className="account-logo-card__file-input"
						onChange={(event) => {
							handleFile(event.target.files);
							event.target.value = "";
						}}
					/>
					<Button
						type="button"
						onClick={() => inputRef.current?.click()}
						disabled={isPending}
					>
						{upload.isPending ? (
							<Loader2 className="account-logo-card__spinner" />
						) : (
							<Upload />
						)}
						{account.logo_url ? "Trocar logo" : "Enviar logo"}
					</Button>
					{account.logo_url && (
						<Button
							type="button"
							variant="outline"
							onClick={() => setConfirmRemove(true)}
							disabled={isPending}
						>
							<Trash2 /> Remover logo
						</Button>
					)}
					{error && (
						<p role="alert" className="form-error-alert">
							{error}
						</p>
					)}
				</div>
			</CardContent>
			<ConfirmDialog
				open={confirmRemove}
				onOpenChange={setConfirmRemove}
				title="Remover logo?"
				description="Os próximos Relatórios de Obra passam a imprimir só o nome da conta no cabeçalho."
				confirmLabel="Remover"
				destructive
				isPending={remove.isPending}
				onConfirm={handleRemove}
			/>
		</Card>
	);
}
